import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { isEqual } from 'lodash'
import { useEffect } from 'react'
import { calculatePricePerUnit } from 'src/pages/TradePage/hooks/utils'
import {
  bestOffersAtom,
  bestOffersModeAtom,
  buyTokenAmountAtom,
  offersAtom,
  pricePerUnitAtom,
  priceSwappedAtom,
  sellTokenAmountAtom,
} from '../../../stores/trade-store'
import { formatBuyTokenAmountMulti, formatSellTokenAmountMulti } from '../utils'
import Big from 'big.js'
import { AnyToken } from 'src/common/types/models/Tokens'
import { usePairOffers } from 'src/common/apollo/hooks/usePairOffers'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'

interface UseSyncBestOffersProps {
  sellToken: AnyToken | undefined
  buyToken: AnyToken | undefined
  sellTokenAmount: Big
  buyTokenAmount: Big
}

const useSyncBestOffers = ({
  sellToken,
  buyToken,
  sellTokenAmount,
  buyTokenAmount,
}: UseSyncBestOffersProps) => {
  const { smartAccountInfo } = useSmartAccount()

  const setSellTokenAmount = useSetAtom(sellTokenAmountAtom)
  const setBuyTokenAmount = useSetAtom(buyTokenAmountAtom)
  const setPricePerUnit = useSetAtom(pricePerUnitAtom)

  const priceSwapped = useAtomValue(priceSwappedAtom)
  const bestOffersMode = useAtomValue(bestOffersModeAtom)
  const bestOffers = useAtomValue(bestOffersAtom)

  const { offers: pairOffers, loading: pairOffersLoading } = usePairOffers({
    accountAddress: smartAccountInfo?.address,
    sellToken,
    buyToken,
    sellTokenAmount,
    buyTokenAmount,
    mode: bestOffersMode,
  })
  const [offers, setOffers] = useAtom(offersAtom)

  useEffect(() => {
    if (!isEqual(pairOffers, offers)) {
      setOffers(pairOffers)
    }
  }, [offers, pairOffers, setOffers])

  useEffect(() => {
    if (
      buyToken &&
      sellToken &&
      sellTokenAmount.gt(0) &&
      bestOffers.length > 0
    ) {
      const _pricePerUnit = calculatePricePerUnit(
        sellTokenAmount,
        buyTokenAmount,
        priceSwapped
      )
      setPricePerUnit(_pricePerUnit)
    }
  }, [
    buyTokenAmount,
    buyToken,
    bestOffers,
    sellToken,
    sellTokenAmount,
    setPricePerUnit,
    priceSwapped,
  ])

  useEffect(() => {
    if (bestOffers.length > 0) {
      if (sellTokenAmount.gt(0) && bestOffersMode === 0) {
        const newBuyTokenAmount = formatBuyTokenAmountMulti(bestOffers)

        if (!newBuyTokenAmount.eq(buyTokenAmount)) {
          setBuyTokenAmount(newBuyTokenAmount)
        }
      }
      if (buyTokenAmount.gt(0) && bestOffersMode === 1) {
        const newSellTokenAmount = formatSellTokenAmountMulti(bestOffers)

        if (!newSellTokenAmount.eq(sellTokenAmount)) {
          setSellTokenAmount(newSellTokenAmount)
        }
      }
    }
  }, [
    bestOffers,
    sellTokenAmount,
    buyTokenAmount,
    setBuyTokenAmount,
    offers,
    bestOffersMode,
    setSellTokenAmount,
  ])

  return { pairOffersLoading }
}

export default useSyncBestOffers
